import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { trackVisit, trackIdentify, trackSave, visitorQuery, appendVisitorFields } from '../lib/visitorTracking';
import useSavePhoto, { filenameFromResponse } from '../hooks/useSavePhoto';
import { Download, X, Upload, Search, Camera, ChevronLeft, ChevronRight } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import Webcam from 'react-webcam';
import CinemaHero from '../components/gallery/cinema/CinemaHero';
import CollectionPills from '../components/gallery/cinema/CollectionPills';
import MasonryGrid from '../components/MasonryGrid';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const SimpleGallery = () => {
  const { simpleToken } = useParams();
  const [gallery, setGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [displayPhotos, setDisplayPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showFaceSearch, setShowFaceSearch] = useState(false);
  const [showWebcam, setShowWebcam] = useState(false);
  const [uploadingReference, setUploadingReference] = useState(false);
  const [searchingFaces, setSearchingFaces] = useState(false);
  const fileInputRef = useRef(null);
  const webcamRef = useRef(null);

  // Sorting and collection state
  const [sortOrder, setSortOrder] = useState('newest');
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);

  // User info modal states
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  // Store pending action as data (not a closure) to avoid stale closure bugs
  const [pendingAction, setPendingAction] = useState(null); // { type: 'download', photo } or { type: 'faceSearch', file }

  const { savePhoto, savePrompt } = useSavePhoto();

  // Refs to always access latest user info (avoids stale closures)
  const userNameRef = useRef(userName);
  const userEmailRef = useRef(userEmail);
  useEffect(() => { userNameRef.current = userName; }, [userName]);
  useEffect(() => { userEmailRef.current = userEmail; }, [userEmail]);

  // Check if user info exists in session storage
  useEffect(() => {
    const storedName = sessionStorage.getItem(`simple_gallery_${simpleToken}_name`);
    const storedEmail = sessionStorage.getItem(`simple_gallery_${simpleToken}_email`);
    if (storedName && storedEmail) {
      setUserName(storedName);
      setUserEmail(storedEmail);
    }
  }, [simpleToken]);

  // Function to check if user info is needed
  const checkUserInfo = (pendingActionData) => {
    if (!userNameRef.current || !userEmailRef.current) {
      // Show modal and store what action to perform after info is collected
      setPendingAction(pendingActionData);
      setShowUserInfoModal(true);
      return false;
    }
    return true;
  };

  // Handle user info submission
  const handleUserInfoSubmit = (e) => {
    e.preventDefault();
    if (!userName.trim() || !userEmail.trim()) {
      toast.error('Please enter both name and email');
      return;
    }

    // Save to session storage
    sessionStorage.setItem(`simple_gallery_${simpleToken}_name`, userName);
    sessionStorage.setItem(`simple_gallery_${simpleToken}_email`, userEmail);
    trackIdentify(`${BACKEND_URL}/api/simple-gallery/${simpleToken}/track`, { name: userName, email: userEmail });

    setShowUserInfoModal(false);

    // Execute pending action using the action data (not a stale closure)
    const action = pendingAction;
    setPendingAction(null);
    if (action) {
      if (action.type === 'download') {
        // Call download directly - userName/userEmail are now set in state
        performDownload(action.photo);
      } else if (action.type === 'faceSearch') {
        searchFacesWithFile(action.file);
      }
    }
  };

  useEffect(() => {
    loadGallery();
    trackVisit(`${BACKEND_URL}/api/simple-gallery/${simpleToken}/track`);
  }, [simpleToken]);

  useEffect(() => {
    // When photos change, update displayPhotos (used after face search filtering)
    setDisplayPhotos(photos);
  }, [photos]);

  const loadGallery = async (currentSortOrder = null) => {
    try {
      setLoading(true);

      // Meta first, so we can honour the gallery's configured default sort.
      const galleryRes = await axios.get(`${BACKEND_URL}/api/simple-gallery/${simpleToken}`);
      setGallery(galleryRes.data);
      setCollections(galleryRes.data.collections || []);

      const resolvedSort = currentSortOrder || galleryRes.data.default_sort || 'newest';
      setSortOrder(resolvedSort);

      const photosRes = await axios.get(`${BACKEND_URL}/api/simple-gallery/${simpleToken}/photos?sort=${resolvedSort}`);
      const photosData = Array.isArray(photosRes.data) ? photosRes.data : [];
      setPhotos(photosData);
      setDisplayPhotos(photosData);
    } catch (error) {
      console.error('Error loading gallery:', error);
      toast.error('Gallery not found');
    } finally {
      setLoading(false);
    }
  };

  // Handle collection change
  const handleCollectionChange = async (collectionSlug) => {
    setSelectedCollection(collectionSlug);

    try {
      const collParam = collectionSlug ? `&collection=${collectionSlug}` : '';
      const response = await axios.get(
        `${BACKEND_URL}/api/simple-gallery/${simpleToken}/photos?sort=${sortOrder}${collParam}`
      );
      const photosData = Array.isArray(response.data) ? response.data : [];
      setPhotos(photosData);
      setDisplayPhotos(photosData);
    } catch (error) {
      console.error('Error filtering by collection:', error);
      toast.error('Failed to filter photos');
    }
  };

  // Handle sort order change - refetch photos with new sort order
  const handleSortChange = async (newSortOrder) => {
    if (newSortOrder === sortOrder) return;

    setSortOrder(newSortOrder);

    try {
      const collParam = selectedCollection ? `&collection=${selectedCollection}` : '';
      const response = await axios.get(
        `${BACKEND_URL}/api/simple-gallery/${simpleToken}/photos?sort=${newSortOrder}${collParam}`
      );
      const photosData = Array.isArray(response.data) ? response.data : [];
      setPhotos(photosData);
      setDisplayPhotos(photosData);
    } catch (error) {
      console.error('Error changing sort order:', error);
      toast.error('Failed to change sort order');
    }
  };

  const handleCaptureSelfie = async () => {
    if (!webcamRef.current) return;

    try {
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        toast.error('Failed to capture photo');
        return;
      }

      // Convert base64 to blob
      const response = await fetch(imageSrc);
      const blob = await response.blob();
      
      // Create file from blob
      const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
      
      setShowWebcam(false);
      await searchFacesWithFile(file);
    } catch (error) {
      console.error('Error capturing selfie:', error);
      toast.error('Failed to capture selfie');
    }
  };

  const handleReferencePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    await searchFacesWithFile(file);
  };

  const searchFacesWithFile = async (file) => {
    // Check if user info is needed first
    if (!checkUserInfo({ type: 'faceSearch', file })) {
      return;
    }
    
    setUploadingReference(true);
    setSearchingFaces(true);

    try {
      const formData = new FormData();
      formData.append('reference_photo', file);
      appendVisitorFields(formData, { name: userNameRef.current, email: userEmailRef.current });

      const response = await axios.post(
        `${BACKEND_URL}/api/simple-gallery/${simpleToken}/search/faces`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const matchedPhotos = response.data.matched_photos || [];
      
      if (matchedPhotos.length === 0) {
        toast.info('No matching faces found in this gallery');
        setDisplayPhotos(photos);
      } else {
        // Filter photos to show only matched ones
        const matchedIds = matchedPhotos.map(m => m.photo_id);
        const filtered = photos.filter(p => matchedIds.includes(p.id));
        setDisplayPhotos(filtered);
        toast.success(`Found ${matchedPhotos.length} photo${matchedPhotos.length !== 1 ? 's' : ''} with matching faces!`);
      }
    } catch (error) {
      console.error('Error searching faces:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' ? errorDetail : 'Failed to search for faces';
      toast.error(errorMessage);
      setDisplayPhotos(photos);
    } finally {
      setUploadingReference(false);
      setSearchingFaces(false);
    }
  };

  const clearFaceSearch = () => {
    setDisplayPhotos(photos);
    setShowFaceSearch(false);
    setShowWebcam(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast.success('Showing all photos');
  };

  // Perform the actual download (called after user info is confirmed)
  const performDownload = async (photo) => {
    try {
      toast.info('Preparing download...');

      // Use axios with responseType: 'blob' to handle redirects properly
      const response = await axios.get(
        `${BACKEND_URL}/api/simple-gallery/${simpleToken}/photo/${photo.id}/download?size=full&${visitorQuery({ name: userNameRef.current, email: userEmailRef.current })}`,
        { responseType: 'blob' }
      );

      const outcome = await savePhoto(
        response.data, filenameFromResponse(response, photo.filename || `photo-${photo.id}.jpg`));
      trackSave(`${BACKEND_URL}/api/simple-gallery/${simpleToken}/track`, {
        name: userNameRef.current, email: userEmailRef.current, photoId: photo.id, outcome,
      });
      if (outcome === 'downloaded') toast.success('Download complete');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download photo');
    }
  };

  const downloadPhoto = async (photo, e) => {
    if (e) {
      e.stopPropagation();
    }

    // Check if user info is needed first
    if (!checkUserInfo({ type: 'download', photo })) {
      return;
    }

    await performDownload(photo);
  };

  const openLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % displayPhotos.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + displayPhotos.length) % displayPhotos.length);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextPhoto();
      if (e.key === 'ArrowLeft') prevPhoto();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, displayPhotos.length]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F4F2EE' }}>
        <div className="text-center">
          <div className="inline-block w-12 h-12 rounded-full animate-spin mb-4" style={{ border: '3px solid rgba(191,33,47,0.25)', borderTopColor: '#BF212F' }}></div>
          <p className="font-tiq-mono tiq-kicker" style={{ fontSize: 11, color: '#8A8884' }}>Loading gallery…</p>
        </div>
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F4F2EE' }}>
        <div className="text-center px-6">
          <h1 className="font-tiq-display uppercase mb-2" style={{ color: '#141414', fontWeight: 900, fontSize: 32, letterSpacing: '-0.02em' }}>Gallery not found</h1>
          <p style={{ color: '#55555C' }}>This gallery does not exist or has been removed.</p>
        </div>
      </div>
    );
  }

  const heroKicker = [
    gallery.event_date
      ? new Date(gallery.event_date).toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
      : null,
    `${photos.length} ${photos.length === 1 ? 'Photo' : 'Photos'}`,
  ].filter(Boolean).join(' · ');

  return (
    <div className="min-h-screen font-tiq-body" style={{ background: '#F4F2EE' }}>
      <Toaster position="top-center" richColors />
      {savePrompt}

      {/* User Info Modal - z-[60] to appear above lightbox */}
      {showUserInfoModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
            <button
              onClick={() => { setShowUserInfoModal(false); setPendingAction(null); }}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Access Gallery</h2>
            <p className="text-slate-600 mb-6">Please enter your details to download photos and use face search.</p>

            <form onSubmit={handleUserInfoSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#BF212F]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#BF212F]"
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-[#BF212F] to-[#F26B38] text-white font-semibold py-3 px-6 rounded-lg hover:opacity-90 transition-all shadow-lg"
              >
                Continue
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Cinematic hero (same as flagship) */}
      <CinemaHero
        imageUrl={gallery.featured_photo_url}
        focalPoint={gallery.featured_photo_focal_point}
        name={gallery.name || 'Gallery'}
        kicker={heroKicker}
        fallback={!gallery.featured_photo_url}
      />

      {/* Compact Find My Photos Section */}
      <div className="bg-slate-50 border-b border-slate-200 py-4">
        <div className="max-w-7xl mx-auto px-4">
          {!showFaceSearch ? (
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <p className="text-slate-600">
                  {displayPhotos.length < photos.length ? (
                    <>
                      Showing <span className="font-bold text-[#BF212F]">{displayPhotos.length}</span> of {photos.length} photos
                      <button
                        onClick={clearFaceSearch}
                        className="ml-3 text-[#BF212F] hover:underline font-medium text-sm"
                      >
                        Show all
                      </button>
                    </>
                  ) : (
                    <><span className="font-bold">{photos.length}</span> photos</>
                  )}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFaceSearch(true)}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#BF212F] to-[#F26B38] text-white px-4 py-2 rounded-lg font-medium text-sm hover:opacity-90 transition-all"
                >
                  <Search className="w-4 h-4" />
                  <span>Find My Photos</span>
                </button>

                <select
                  value={sortOrder}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#BF212F]"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Search className="w-5 h-5 text-[#BF212F]" />
                  Find Yourself in Photos
                </h3>
                <button
                  onClick={clearFaceSearch}
                  className="text-slate-500 hover:text-slate-700 text-sm font-medium"
                >
                  Cancel
                </button>
              </div>

              {!showWebcam ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="cursor-pointer">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleReferencePhotoUpload}
                      disabled={uploadingReference}
                      className="hidden"
                    />
                    <div className="inline-flex items-center gap-2 bg-[#BF212F] hover:bg-[#a51c28] text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors">
                      {uploadingReference ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Searching...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          <span>Upload Photo</span>
                        </>
                      )}
                    </div>
                  </label>

                  <button
                    onClick={() => setShowWebcam(true)}
                    disabled={uploadingReference}
                    className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
                  >
                    <Camera className="w-4 h-4" />
                    <span>Take Selfie</span>
                  </button>

                  {searchingFaces && (
                    <div className="inline-flex items-center gap-2 text-slate-600 text-sm">
                      <div className="w-4 h-4 border-2 border-[#BF212F] border-t-transparent rounded-full animate-spin"></div>
                      <span>Searching {photos.length} photos...</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="relative rounded-lg overflow-hidden border-2 border-[#BF212F] bg-black max-w-md">
                    <Webcam
                      ref={webcamRef}
                      audio={false}
                      screenshotFormat="image/jpeg"
                      videoConstraints={{
                        width: 1280,
                        height: 720,
                        facingMode: "user"
                      }}
                      className="w-full"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleCaptureSelfie}
                      disabled={uploadingReference}
                      className="inline-flex items-center gap-2 bg-[#BF212F] hover:bg-[#a51c28] text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
                    >
                      <Camera className="w-4 h-4" />
                      <span>Capture & Search</span>
                    </button>
                    <button
                      onClick={() => setShowWebcam(false)}
                      className="px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg font-medium text-sm hover:bg-slate-100 transition-colors"
                    >
                      Back
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Collection Pills (light, carbon-fill active) */}
      {collections.length > 0 && (
        <div style={{ padding: '12px clamp(12px,3vw,24px)' }}>
          <CollectionPills
            collections={collections}
            activeSlug={selectedCollection}
            onSelect={(slug) => handleCollectionChange(slug)}
            totalCount={photos.length}
            totalLabel="All"
            theme="light"
          />
        </div>
      )}

      {/* Photo Grid — Cinema masonry (same as the flagship share link), light theme */}
      <div style={{ padding: '4px clamp(8px,2vw,16px) clamp(24px,4vw,40px)' }}>
        {displayPhotos.length === 0 ? (
          <div className="text-center" style={{ padding: '64px 20px' }}>
            <p className="font-tiq-mono tiq-kicker" style={{ fontSize: 12, color: '#8A8884' }}>
              {photos.length === 0 ? 'No photos in this gallery yet' : 'No matching photos found'}
            </p>
          </div>
        ) : (
          <MasonryGrid
            photos={displayPhotos}
            onPhotoClick={(_photo, index) => openLightbox(index)}
            onDownload={(photo) => downloadPhoto(photo)}
            backendUrl={BACKEND_URL}
            theme="light"
          />
        )}
      </div>

      {/* Lightbox - 2026 UX Design */}
      {lightboxOpen && displayPhotos[currentPhotoIndex] && (
        <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-2xl">
          {/* Top Controls Bar */}
          <div className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/70 to-transparent p-4">
            <div className="flex items-center justify-between">
              {/* Left - Photo Counter */}
              <div className="bg-white/10 backdrop-blur-xl text-white px-4 py-2 rounded-2xl text-sm font-medium">
                {currentPhotoIndex + 1} / {displayPhotos.length}
              </div>

              {/* Right - Close Button */}
              <button
                onClick={closeLightbox}
                className="bg-white/10 backdrop-blur-xl text-white hover:bg-white/20 rounded-2xl p-2.5 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Main Image Container */}
          <div
            className="absolute left-0 right-0 flex items-center justify-center"
            style={{
              top: '70px',
              bottom: '120px',
              padding: '8px'
            }}
          >
            <img
              src={displayPhotos[currentPhotoIndex].preview_url || displayPhotos[currentPhotoIndex].original_url}
              alt={displayPhotos[currentPhotoIndex].filename || `Photo ${currentPhotoIndex + 1}`}
              className="max-w-full max-h-full object-contain select-none"
              draggable={false}
            />
          </div>

          {/* Navigation Arrows */}
          {displayPhotos.length > 1 && (
            <>
              <button
                onClick={prevPhoto}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-40 bg-white/10 backdrop-blur-xl text-white hover:bg-white/20 rounded-2xl w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center transition-all"
              >
                <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
              </button>
              <button
                onClick={nextPhoto}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-40 bg-white/10 backdrop-blur-xl text-white hover:bg-white/20 rounded-2xl w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center transition-all"
              >
                <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
              </button>
            </>
          )}

          {/* Bottom Controls Bar */}
          <div className="absolute bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-black/70 to-transparent p-4 pb-6">
            {/* Download Button */}
            <div className="flex justify-center mb-3">
              <button
                onClick={(e) => downloadPhoto(displayPhotos[currentPhotoIndex], e)}
                className="bg-gradient-to-r from-[#BF212F] to-[#F26B38] hover:opacity-90 text-white px-6 py-3 rounded-2xl flex items-center gap-2 transition-all shadow-lg font-medium"
              >
                <Download className="w-5 h-5" />
                <span>Download</span>
              </button>
            </div>

            {/* Filename */}
            <p className="text-center text-white/80 text-sm truncate px-4">
              {String(displayPhotos[currentPhotoIndex]?.filename || '')}
            </p>
          </div>
        </div>
      )}

      {/* Footer (light) */}
      <footer className="flex flex-col items-center gap-[14px]" style={{ borderTop: '1px solid #E2DED6', padding: 'clamp(28px,4vw,40px) 24px' }}>
        <div className="tiq-signal-bar" style={{ width: 120, height: 4, borderRadius: 2 }} />
        <a href="https://turbo.net.au/?utm_source=iq.turbo.net.au&utm_medium=referral&utm_campaign=client_gallery&utm_content=simple_footer" target="_blank" rel="noopener" className="font-tiq-mono tiq-kicker" style={{ fontSize: 11, letterSpacing: '0.16em', color: '#8A8884' }}>
          Powered by Turbo 360 · Turbo IQ
        </a>
      </footer>
    </div>
  );
};

export default SimpleGallery;
